import React, { useCallback, useMemo } from 'react'
import { Col, Row } from 'react-bootstrap'
import { StandardSelect } from 'components'
import { properCase } from 'utils/properCase'
import {
  currentGradeLevelGroups,
  seekingDegreeGroups,
} from 'services/options-lists'
import mirrorKeys from 'utils/mirror-keys'

const GradeDegree = ({
  translate,
  defaultValue,
  criteria,
  multiples,
  setMultiples,
  onFilter,
  filters,
  setFilters,
}) => {
  const gradeLevelGrouped = mirrorKeys(Object.keys(currentGradeLevelGroups))
  const seekingDegreeGrouped = mirrorKeys(Object.keys(seekingDegreeGroups))
  const customDegreeSort = []

  if (!defaultValue?.gradeLevelList) {
    defaultValue.gradeLevelList = []
  }

  if (!defaultValue?.seekingDegreeList) {
    defaultValue.seekingDegreeList = []
  }

  gradeLevelGrouped.forEach((group) =>
    customDegreeSort.push(...currentGradeLevelGroups[group.value])
  )
  customDegreeSort.push('Not Enrolled')
  customDegreeSort.push('On Academic Break')

  const options = useMemo(
    () => [
      {
        options: gradeLevelGrouped,
      },
      {
        options: customDegreeSort.map((item) => ({
          value: item,
          label: properCase(item),
        })),
        header: <hr className="border-secondary my-1" />,
      },
    ],
    [customDegreeSort, gradeLevelGrouped]
  )

  const optionsDegree = useMemo(
    () => [
      {
        options: seekingDegreeGrouped,
      },
      {
        options: criteria['degreeSoughts'].map((item) => ({
          value: item,
          label: properCase(item),
        })),
        header: <hr className="border-secondary my-1" />,
      },
    ],
    [criteria, seekingDegreeGrouped]
  )

  const handleGradeLevelFilter = useCallback(
    (values) => {
      const gradeFilterList = []

      values && values.forEach((item) => {
        const grades =
          item.value ===
          gradeLevelGrouped.find(
            (group) =>
              group.value === item.value &&
              gradeFilterList.push(...currentGradeLevelGroups[group.value])
          )
        if (!Object.keys(currentGradeLevelGroups).includes(item.value)) {
          gradeFilterList.push(item.value)
        }
      })

      setMultiples({
        ...multiples,
        gradeLevelList: values,
        gradeLevel: gradeFilterList,
      })
      onFilter({
        gradeLevelList: values,
        gradeLevel: gradeFilterList,
      })

      if (gradeFilterList.length === 0) {
        defaultValue.gradeLevelList = null
        defaultValue.gradeLevel = null
        setFilters(
          filters.filter((f) => f !== 'gradeLevelList' && f !== 'gradeLevel')
        )
      } else {
        if (!filters.includes('gradeLevelList', 'gradeLevel')) {
          setFilters(filters.concat('gradeLevelList', 'gradeLevel'))
        }
      }
    },
    [
      defaultValue,
      filters,
      gradeLevelGrouped,
      multiples,
      onFilter,
      setFilters,
      setMultiples,
    ]
  )

  const handleSeekingDegreeFilter = useCallback(
    (values) => {
      const degreeFilterList = []

      values && values.forEach((item) => {
        const degree =
          item.value ===
          seekingDegreeGrouped.find(
            (group) =>
              group.value === item.value &&
              degreeFilterList.push(...seekingDegreeGroups[group.value])
          )
        if (!Object.keys(seekingDegreeGroups).includes(item.value)) {
          degreeFilterList.push(item.value)
        }
      })

      setMultiples({
        ...multiples,
        seekingDegreeList: values,
        seekingDegree: degreeFilterList,
      })
      onFilter({
        seekingDegreeList: values,
        seekingDegree: degreeFilterList,
      })

      if (degreeFilterList.length === 0) {
        defaultValue.seekingDegreeList = null
        defaultValue.seekingDegree = null
        setFilters(
          filters.filter(
            (f) => f !== 'seekingDegreeList' && f !== 'seekingDegree'
          )
        )
      } else {
        if (!filters.includes('seekingDegreeList', 'seekingDegree')) {
          setFilters(filters.concat('seekingDegreeList', 'seekingDegree'))
        }
      }
    },
    [
      defaultValue,
      filters,
      multiples,
      onFilter,
      seekingDegreeGrouped,
      setFilters,
      setMultiples,
    ]
  )

  const formatGroupLabel = useCallback(({ header }) => header || null, [])
  const styles = useMemo(
    () => ({ group: () => ({ paddingBottom: 0, paddingTop: 0 }) }),
    []
  )

  return (
    <>
      <Row>
        <Col>
          <div>{translate('currentGradeLevels')}</div>
          <StandardSelect
            isMulti
            placeholder=""
            isClearable
            onChange={(e) => handleGradeLevelFilter(e)}
            value={defaultValue?.gradeLevelList}
            {...{
              formatGroupLabel,
              styles,
              options,
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div>{translate('degreeSeeking')}</div>
          <StandardSelect
            isMulti
            placeholder=""
            isClearable
            onChange={(e) => handleSeekingDegreeFilter(e)}
            value={defaultValue?.seekingDegreeList}
            {...{
              formatGroupLabel,
              styles,
              options: optionsDegree,
            }}
          />
        </Col>
      </Row>
    </>
  )
}

export default GradeDegree
